import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateEmailMarketingEnabled } from "../../../helpers/api/crm";
import type {
  Customer360Snapshot,
  EmailMarketingProfile,
} from "../../../types/crm";
import { EnableEmailMarketingModal } from "../EnableEmailMarketingModal";

interface Props {
  customerId: string;
  enabled: boolean;
  profile: EmailMarketingProfile;
}

export function EmailMarketingSection({
  customerId,
  enabled,
  profile,
}: Props) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  // Disable is a one-click action (no form needed). Enable opens the modal so
  // the admin can confirm/fill the hotel-row fields (and CB/Mews creds) the
  // backend needs to create the row.
  const disable = useMutation({
    mutationFn: () => updateEmailMarketingEnabled(customerId, false),
    onSuccess: () => {
      qc.setQueryData<Customer360Snapshot>(
        ["crm", "customer360", customerId],
        (old) =>
          old
            ? {
                ...old,
                customer: { ...old.customer, emailMarketingEnabled: false },
              }
            : old,
      );
    },
  });

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Email Marketing
        </h2>
        <p className="text-[10px] text-content-tertiary mt-0.5">
          controls the Email Marketing entry point in the customer app
        </p>
      </div>

      <div className="flex-1 p-4 flex items-center justify-between gap-3">
        <span className="text-sm text-content-secondary">
          {enabled ? "Enabled" : "Disabled"}
        </span>
        <div className="flex items-center gap-2">
          {disable.isError && (
            <span className="text-[11px] text-rose-600">Failed to save</span>
          )}
          {disable.isPending && (
            <span className="text-[11px] text-content-tertiary">Saving…</span>
          )}
          {enabled ? (
            <button
              type="button"
              disabled={disable.isPending}
              onClick={() => disable.mutate()}
              className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary rounded-md border border-border-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
            >
              Disable
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Enable Email Marketing
            </button>
          )}
        </div>
      </div>

      {modalOpen && (
        <EnableEmailMarketingModal
          customerId={customerId}
          profile={profile}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}
